import React, { useState, useEffect } from 'react';
import { BomberosHeader } from '../../components/BomberosHeader';
import { useParams, useNavigate } from 'react-router-dom';
import { bomberosMaterialMayorAPI, bomberosCompaniaAPI, bomberosInventarioAPI, bomberosItemsCatalogoAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '../../components/ui/command';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '../../components/ui/popover';
import { toast } from 'sonner';
import {
    ArrowLeft,
    Truck,
    Edit,
    Calendar,
    Droplets,
    AlertTriangle,
    CheckCircle,
    XCircle,
    Wrench,
    Building2,
    FileText,
    Hash,
    Gauge,
    Package,
    Plus,
    Search,
    Trash2,
    Loader2,
    Check,
    ChevronsUpDown,
} from 'lucide-react';

// Categorías de items
const CATEGORIAS = {
    "herramientas": "Herramientas",
    "epp": "EPP / Protección",
    "mangueras": "Mangueras y Pitones",
    "equipos_medicos": "Equipos Médicos",
    "extintores": "Extintores",
    "escaleras": "Escaleras",
    "comunicaciones": "Comunicaciones",
    "iluminacion": "Iluminación",
    "rescate": "Equipos de Rescate",
    "otros": "Otros",
};

const ESTADOS_ITEM = {
    "bueno": { label: "Bueno", color: "bg-green-500" },
    "regular": { label: "Regular", color: "bg-yellow-500" },
    "malo": { label: "Malo", color: "bg-red-500" },
    "vencido": { label: "Vencido", color: "bg-gray-500" },
};

export default function MaterialMayorDetallePage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [vehiculo, setVehiculo] = useState(null);
    const [compania, setCompania] = useState(null);
    const [activeTab, setActiveTab] = useState('info');
    
    // Inventario states
    const [inventario, setInventario] = useState([]);
    const [loadingInventario, setLoadingInventario] = useState(false);
    const [catalogoItems, setCatalogoItems] = useState([]);
    const [showAddItemDialog, setShowAddItemDialog] = useState(false);
    const [showEditItemDialog, setShowEditItemDialog] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [saving, setSaving] = useState(false);
    const [searchItem, setSearchItem] = useState('');
    const [openItemSearch, setOpenItemSearch] = useState(false);
    
    // Form para agregar/editar item
    const [itemForm, setItemForm] = useState({
        item_id: '',
        item_nombre: '',
        item_categoria: 'otros',
        item_descripcion: '',
        cantidad: 1,
        estado: 'bueno',
        codigo_serie: '',
        fecha_vencimiento: '',
        observaciones: '',
        ubicacion_vehiculo: '',
    });

    useEffect(() => {
        loadVehiculo();
    }, [id]);

    useEffect(() => {
        if (activeTab === 'inventario' && vehiculo) {
            loadInventario();
            loadCatalogo();
        }
    }, [activeTab, vehiculo]);

    const loadVehiculo = async () => {
        try {
            setLoading(true);
            const response = await bomberosMaterialMayorAPI.getById(id);
            setVehiculo(response.data);
            
            if (response.data.compania_id) {
                try {
                    const companiaRes = await bomberosCompaniaAPI.getById(response.data.compania_id);
                    setCompania(companiaRes.data);
                } catch (e) {
                    console.error('Error loading compania:', e);
                }
            }
        } catch (err) {
            console.error('Error loading vehiculo:', err);
            toast.error('Error al cargar el vehículo');
            navigate('/bomberos/material-mayor');
        } finally {
            setLoading(false);
        }
    };

    const loadInventario = async () => {
        try {
            setLoadingInventario(true);
            const res = await bomberosInventarioAPI.getByVehiculo(id);
            setInventario(res.data);
        } catch (err) {
            console.error('Error loading inventario:', err);
            toast.error('Error al cargar inventario');
        } finally {
            setLoadingInventario(false);
        }
    };

    const loadCatalogo = async () => {
        try {
            const res = await bomberosItemsCatalogoAPI.getAll();
            setCatalogoItems(res.data);
        } catch (err) {
            console.error('Error loading catalogo:', err);
        }
    };

    const resetItemForm = () => {
        setItemForm({
            item_id: '',
            item_nombre: '',
            item_categoria: 'otros',
            item_descripcion: '',
            cantidad: 1,
            estado: 'bueno',
            codigo_serie: '',
            fecha_vencimiento: '',
            observaciones: '',
            ubicacion_vehiculo: '',
        });
        setSearchItem('');
    };

    const handleAddItem = async () => {
        if (!itemForm.item_id && !itemForm.item_nombre) {
            toast.error('Seleccione un item o ingrese un nombre para crear uno nuevo');
            return;
        }

        setSaving(true);
        try {
            await bomberosInventarioAPI.addItem(id, itemForm);
            toast.success('Item agregado al inventario');
            setShowAddItemDialog(false);
            resetItemForm();
            loadInventario();
            loadCatalogo();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al agregar item');
        } finally {
            setSaving(false);
        }
    };

    const handleUpdateItem = async () => {
        if (!selectedItem) return;

        setSaving(true);
        try {
            await bomberosInventarioAPI.updateItem(selectedItem.id, {
                cantidad: itemForm.cantidad,
                estado: itemForm.estado,
                codigo_serie: itemForm.codigo_serie,
                fecha_vencimiento: itemForm.fecha_vencimiento,
                observaciones: itemForm.observaciones,
                ubicacion_vehiculo: itemForm.ubicacion_vehiculo,
            });
            toast.success('Item actualizado');
            setShowEditItemDialog(false);
            setSelectedItem(null);
            resetItemForm();
            loadInventario();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al actualizar item');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteItem = async (invItem) => {
        if (!window.confirm(`¿Eliminar "${invItem.item_nombre}" del inventario?`)) return;

        try {
            await bomberosInventarioAPI.deleteItem(invItem.id);
            toast.success('Item eliminado del inventario');
            loadInventario();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al eliminar');
        }
    };

    const openEditDialog = (invItem) => {
        setSelectedItem(invItem);
        setItemForm({
            item_id: invItem.item_id,
            item_nombre: invItem.item_nombre,
            item_categoria: invItem.item_categoria,
            item_descripcion: '',
            cantidad: invItem.cantidad,
            estado: invItem.estado,
            codigo_serie: invItem.codigo_serie || '',
            fecha_vencimiento: invItem.fecha_vencimiento || '',
            observaciones: invItem.observaciones || '',
            ubicacion_vehiculo: invItem.ubicacion_vehiculo || '',
        });
        setShowEditItemDialog(true);
    };

    const selectItemFromCatalogo = (item) => {
        setItemForm(prev => ({
            ...prev,
            item_id: item.id,
            item_nombre: item.nombre,
            item_categoria: item.categoria,
        }));
        setSearchItem(item.nombre);
        setOpenItemSearch(false);
    };

    // Filtrar items del catálogo según búsqueda
    const filteredCatalogoItems = catalogoItems.filter(item =>
        item.nombre.toLowerCase().includes(searchItem.toLowerCase())
    );

    const getEstadoBadge = (vehiculo) => {
        const estado = vehiculo.estado_operativo || vehiculo.estado;
        const estados = {
            operativo: { color: 'bg-green-500', icon: CheckCircle, label: 'Operativo' },
            en_mantenimiento: { color: 'bg-yellow-500', icon: Wrench, label: 'En Mantenimiento' },
            fuera_servicio: { color: 'bg-red-500', icon: XCircle, label: 'Fuera de Servicio' },
        };
        const config = estados[estado] || { color: 'bg-gray-500', icon: AlertTriangle, label: estado || 'Sin estado' };
        const IconComponent = config.icon;
        return (
            <Badge className={`${config.color} text-white flex items-center gap-1`}>
                <IconComponent className="h-3 w-3" />
                {config.label}
            </Badge>
        );
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'No registrada';
        return new Date(dateStr).toLocaleDateString('es-CL');
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background p-6">
                <div className="max-w-4xl mx-auto space-y-6">
                    <Skeleton className="h-12 w-48" />
                    <Skeleton className="h-64 w-full" />
                    <Skeleton className="h-48 w-full" />
                </div>
            </div>
        );
    }

    if (!vehiculo) {
        return (
            <div className="min-h-screen bg-background p-6">
                <div className="max-w-4xl mx-auto text-center py-12">
                    <AlertTriangle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h2 className="text-xl font-semibold">Vehículo no encontrado</h2>
                    <Button onClick={() => navigate('/bomberos/material-mayor')} className="mt-4">
                        Volver a la lista
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <>
            <BomberosHeader />
            <div className="min-h-screen bg-background pt-14">
            {/* Header */}
            <header className="sticky top-0 z-10 bg-card border-b border-border shadow-sm">
                <div className="container mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => navigate('/bomberos/material-mayor')}
                                data-testid="back-btn"
                            >
                                <ArrowLeft className="h-5 w-5" />
                            </Button>
                            <div className="flex items-center gap-3">
                                <div className="bg-primary/10 p-2 rounded-lg">
                                    <Truck className="h-6 w-6 text-primary" />
                                </div>
                                <div>
                                    <h1 className="text-xl font-bold">{vehiculo.codigo}</h1>
                                    <p className="text-sm text-muted-foreground">
                                        {vehiculo.marca} {vehiculo.modelo}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <Button 
                            onClick={() => navigate(`/bomberos/material-mayor/${id}/editar`)}
                            data-testid="editar-btn"
                        >
                            <Edit className="h-4 w-4 mr-2" />
                            Editar
                        </Button>
                    </div>
                </div>
            </header>

            <main className="container mx-auto px-4 py-6 max-w-5xl">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                    <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="info" className="flex items-center gap-2">
                            <Truck className="h-4 w-4" />
                            Información
                        </TabsTrigger>
                        <TabsTrigger value="inventario" className="flex items-center gap-2">
                            <Package className="h-4 w-4" />
                            Inventario ({inventario.length})
                        </TabsTrigger>
                    </TabsList>

                    {/* TAB: Información del Vehículo */}
                    <TabsContent value="info" className="space-y-6">
                        {/* Información Principal */}
                        <Card data-testid="info-principal-card">
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <CardTitle className="flex items-center gap-2">
                                        <Truck className="h-5 w-5" />
                                        Información del Vehículo
                                    </CardTitle>
                                    {getEstadoBadge(vehiculo)}
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    <div className="space-y-1">
                                        <p className="text-sm text-muted-foreground">Código</p>
                                        <p className="font-medium flex items-center gap-2">
                                            <Hash className="h-4 w-4 text-muted-foreground" />
                                            {vehiculo.codigo}
                                        </p>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-sm text-muted-foreground">Patente</p>
                                        <p className="font-medium">{vehiculo.patente || 'Sin patente'}</p>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-sm text-muted-foreground">Tipo</p>
                                        <p className="font-medium capitalize">{vehiculo.tipo || 'No especificado'}</p>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-sm text-muted-foreground">Marca</p>
                                        <p className="font-medium">{vehiculo.marca || 'No especificada'}</p>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-sm text-muted-foreground">Modelo</p>
                                        <p className="font-medium">{vehiculo.modelo || 'No especificado'}</p>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-sm text-muted-foreground">Año</p>
                                        <p className="font-medium">{vehiculo.anio || vehiculo.año || 'No especificado'}</p>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-sm text-muted-foreground">Compañía</p>
                                        <p className="font-medium flex items-center gap-2">
                                            <Building2 className="h-4 w-4 text-muted-foreground" />
                                            {vehiculo.compania_nombre || compania?.nombre || 'Sin asignar'}
                                        </p>
                                    </div>
                                    {vehiculo.capacidad_agua && (
                                        <div className="space-y-1">
                                            <p className="text-sm text-muted-foreground">Capacidad de Agua</p>
                                            <p className="font-medium flex items-center gap-2">
                                                <Droplets className="h-4 w-4 text-blue-500" />
                                                {vehiculo.capacidad_agua} L
                                            </p>
                                        </div>
                                    )}
                                    {vehiculo.kilometraje && (
                                        <div className="space-y-1">
                                            <p className="text-sm text-muted-foreground">Kilometraje</p>
                                            <p className="font-medium flex items-center gap-2">
                                                <Gauge className="h-4 w-4 text-muted-foreground" />
                                                {vehiculo.kilometraje.toLocaleString()} km
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Fechas y Vencimientos */}
                        <Card data-testid="fechas-card">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Calendar className="h-5 w-5" />
                                    Fechas y Vencimientos
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    <div className="space-y-1">
                                        <p className="text-sm text-muted-foreground">Fecha de Adquisición</p>
                                        <p className="font-medium">{formatDate(vehiculo.fecha_adquisicion)}</p>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-sm text-muted-foreground">Revisión Técnica</p>
                                        <div className="flex items-center gap-2">
                                            <p className="font-medium">{formatDate(vehiculo.fecha_revision_tecnica)}</p>
                                            {vehiculo.dias_para_revision !== null && vehiculo.dias_para_revision < 30 && (
                                                <Badge variant="destructive" className="text-xs">
                                                    <AlertTriangle className="h-3 w-3 mr-1" />
                                                    {vehiculo.dias_para_revision} días
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-sm text-muted-foreground">Permiso de Circulación</p>
                                        <p className="font-medium">{formatDate(vehiculo.fecha_permiso_circulacion)}</p>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-sm text-muted-foreground">Seguro</p>
                                        <p className="font-medium">{formatDate(vehiculo.fecha_seguro)}</p>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-sm text-muted-foreground">Último Mantenimiento</p>
                                        <p className="font-medium">{formatDate(vehiculo.fecha_ultimo_mantenimiento)}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Alertas */}
                        {vehiculo.alertas && vehiculo.alertas.length > 0 && (
                            <Card className="border-yellow-500" data-testid="alertas-card">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-yellow-600">
                                        <AlertTriangle className="h-5 w-5" />
                                        Alertas
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <ul className="space-y-2">
                                        {vehiculo.alertas.map((alerta, idx) => (
                                            <li key={idx} className="flex items-center gap-2 text-yellow-700">
                                                <AlertTriangle className="h-4 w-4" />
                                                {alerta}
                                            </li>
                                        ))}
                                    </ul>
                                </CardContent>
                            </Card>
                        )}

                        {/* Observaciones */}
                        {vehiculo.observaciones && (
                            <Card data-testid="observaciones-card">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <FileText className="h-5 w-5" />
                                        Observaciones
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-muted-foreground whitespace-pre-wrap">
                                        {vehiculo.observaciones}
                                    </p>
                                </CardContent>
                            </Card>
                        )}
                    </TabsContent>

                    {/* TAB: Inventario */}
                    <TabsContent value="inventario" className="space-y-6">
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle className="flex items-center gap-2">
                                            <Package className="h-5 w-5" />
                                            Inventario del Vehículo
                                        </CardTitle>
                                        <CardDescription>
                                            Equipos, herramientas y materiales del vehículo
                                        </CardDescription>
                                    </div>
                                    <Button onClick={() => { resetItemForm(); setShowAddItemDialog(true); }} data-testid="add-item-btn">
                                        <Plus className="h-4 w-4 mr-2" />
                                        Agregar Item
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {loadingInventario ? (
                                    <div className="space-y-2">
                                        <Skeleton className="h-12 w-full" />
                                        <Skeleton className="h-12 w-full" />
                                        <Skeleton className="h-12 w-full" />
                                    </div>
                                ) : inventario.length === 0 ? (
                                    <div className="text-center py-12 text-muted-foreground">
                                        <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                        <p>No hay items en el inventario</p>
                                        <p className="text-sm">Agregue equipos, herramientas y materiales</p>
                                    </div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Item</TableHead>
                                                <TableHead>Categoría</TableHead>
                                                <TableHead className="text-center">Cantidad</TableHead>
                                                <TableHead>Estado</TableHead>
                                                <TableHead>Vencimiento</TableHead>
                                                <TableHead>Ubicación</TableHead>
                                                <TableHead className="text-right">Acciones</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {inventario.map((inv) => (
                                                <TableRow key={inv.id}>
                                                    <TableCell>
                                                        <div>
                                                            <p className="font-medium">{inv.item_nombre}</p>
                                                            {inv.codigo_serie && (
                                                                <p className="text-xs text-muted-foreground">S/N: {inv.codigo_serie}</p>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge variant="outline">
                                                            {CATEGORIAS[inv.item_categoria] || inv.item_categoria}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell className="text-center font-medium">
                                                        {inv.cantidad} {inv.item_unidad !== 'unidad' && inv.item_unidad}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge className={`${ESTADOS_ITEM[inv.estado]?.color || 'bg-gray-500'} text-white`}>
                                                            {ESTADOS_ITEM[inv.estado]?.label || inv.estado}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        {inv.fecha_vencimiento ? (
                                                            <span className={new Date(inv.fecha_vencimiento) < new Date() ? 'text-red-500 font-medium' : ''}>
                                                                {new Date(inv.fecha_vencimiento).toLocaleDateString('es-CL')}
                                                            </span>
                                                        ) : '-'}
                                                    </TableCell>
                                                    <TableCell className="max-w-32 truncate">
                                                        {inv.ubicacion_vehiculo || '-'}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <div className="flex justify-end gap-1">
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={() => openEditDialog(inv)}
                                                                title="Editar"
                                                            >
                                                                <Edit className="h-4 w-4" />
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={() => handleDeleteItem(inv)}
                                                                className="text-destructive"
                                                                title="Eliminar"
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </main>

            {/* Dialog: Agregar Item */}
            <Dialog open={showAddItemDialog} onOpenChange={setShowAddItemDialog}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Agregar Item al Inventario</DialogTitle>
                        <DialogDescription>
                            Busque un item existente o cree uno nuevo
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        {/* Búsqueda de Item */}
                        <div className="space-y-2">
                            <Label>Item *</Label>
                            <Popover open={openItemSearch} onOpenChange={setOpenItemSearch}>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        role="combobox"
                                        aria-expanded={openItemSearch}
                                        className="w-full justify-between"
                                    >
                                        {itemForm.item_nombre || "Buscar o crear item..."}
                                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-full p-0" align="start">
                                    <Command>
                                        <CommandInput 
                                            placeholder="Buscar item..." 
                                            value={searchItem}
                                            onValueChange={setSearchItem}
                                        />
                                        <CommandList>
                                            <CommandEmpty>
                                                <div className="p-2 text-center">
                                                    <p className="text-sm text-muted-foreground mb-2">No se encontró "{searchItem}"</p>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => {
                                                            setItemForm(prev => ({
                                                                ...prev,
                                                                item_id: '',
                                                                item_nombre: searchItem,
                                                            }));
                                                            setOpenItemSearch(false);
                                                        }}
                                                    >
                                                        <Plus className="h-4 w-4 mr-1" />
                                                        Crear "{searchItem}"
                                                    </Button>
                                                </div>
                                            </CommandEmpty>
                                            <CommandGroup heading="Items existentes">
                                                {filteredCatalogoItems.slice(0, 10).map((item) => (
                                                    <CommandItem
                                                        key={item.id}
                                                        value={item.nombre}
                                                        onSelect={() => selectItemFromCatalogo(item)}
                                                    >
                                                        <Check
                                                            className={`mr-2 h-4 w-4 ${itemForm.item_id === item.id ? "opacity-100" : "opacity-0"}`}
                                                        />
                                                        <div className="flex-1">
                                                            <p>{item.nombre}</p>
                                                            <p className="text-xs text-muted-foreground">{CATEGORIAS[item.categoria]}</p>
                                                        </div>
                                                    </CommandItem>
                                                ))}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                        </div>

                        {/* Si es item nuevo, mostrar campos adicionales */}
                        {itemForm.item_nombre && !itemForm.item_id && (
                            <div className="space-y-2 p-3 bg-muted/50 rounded-md">
                                <p className="text-sm font-medium">Nuevo item: {itemForm.item_nombre}</p>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <Label className="text-xs">Categoría</Label>
                                        <Select
                                            value={itemForm.item_categoria}
                                            onValueChange={(v) => setItemForm(prev => ({ ...prev, item_categoria: v }))}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {Object.entries(CATEGORIAS).map(([key, label]) => (
                                                    <SelectItem key={key} value={key}>{label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Cantidad y Estado */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Cantidad</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    value={itemForm.cantidad}
                                    onChange={(e) => setItemForm(prev => ({ ...prev, cantidad: parseInt(e.target.value) || 1 }))}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Estado</Label>
                                <Select
                                    value={itemForm.estado}
                                    onValueChange={(v) => setItemForm(prev => ({ ...prev, estado: v }))}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(ESTADOS_ITEM).map(([key, { label }]) => (
                                            <SelectItem key={key} value={key}>{label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Código Serie y Vencimiento */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Código/Serie (opcional)</Label>
                                <Input
                                    value={itemForm.codigo_serie}
                                    onChange={(e) => setItemForm(prev => ({ ...prev, codigo_serie: e.target.value }))}
                                    placeholder="S/N o código"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Fecha Vencimiento (opcional)</Label>
                                <Input
                                    type="date"
                                    value={itemForm.fecha_vencimiento}
                                    onChange={(e) => setItemForm(prev => ({ ...prev, fecha_vencimiento: e.target.value }))}
                                />
                            </div>
                        </div>

                        {/* Ubicación */}
                        <div className="space-y-2">
                            <Label>Ubicación en vehículo (opcional)</Label>
                            <Input
                                value={itemForm.ubicacion_vehiculo}
                                onChange={(e) => setItemForm(prev => ({ ...prev, ubicacion_vehiculo: e.target.value }))}
                                placeholder="Ej: Compartimento lateral izquierdo"
                            />
                        </div>

                        {/* Observaciones */}
                        <div className="space-y-2">
                            <Label>Observaciones (opcional)</Label>
                            <Textarea
                                value={itemForm.observaciones}
                                onChange={(e) => setItemForm(prev => ({ ...prev, observaciones: e.target.value }))}
                                rows={2}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAddItemDialog(false)}>Cancelar</Button>
                        <Button onClick={handleAddItem} disabled={saving}>
                            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                            Agregar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog: Editar Item */}
            <Dialog open={showEditItemDialog} onOpenChange={setShowEditItemDialog}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Editar Item</DialogTitle>
                        <DialogDescription>
                            {selectedItem?.item_nombre}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        {/* Cantidad y Estado */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Cantidad</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    value={itemForm.cantidad}
                                    onChange={(e) => setItemForm(prev => ({ ...prev, cantidad: parseInt(e.target.value) || 1 }))}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Estado</Label>
                                <Select
                                    value={itemForm.estado}
                                    onValueChange={(v) => setItemForm(prev => ({ ...prev, estado: v }))}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(ESTADOS_ITEM).map(([key, { label }]) => (
                                            <SelectItem key={key} value={key}>{label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Código Serie y Vencimiento */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Código/Serie (opcional)</Label>
                                <Input
                                    value={itemForm.codigo_serie}
                                    onChange={(e) => setItemForm(prev => ({ ...prev, codigo_serie: e.target.value }))}
                                    placeholder="S/N o código"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Fecha Vencimiento (opcional)</Label>
                                <Input
                                    type="date"
                                    value={itemForm.fecha_vencimiento}
                                    onChange={(e) => setItemForm(prev => ({ ...prev, fecha_vencimiento: e.target.value }))}
                                />
                            </div>
                        </div>

                        {/* Ubicación */}
                        <div className="space-y-2">
                            <Label>Ubicación en vehículo (opcional)</Label>
                            <Input
                                value={itemForm.ubicacion_vehiculo}
                                onChange={(e) => setItemForm(prev => ({ ...prev, ubicacion_vehiculo: e.target.value }))}
                                placeholder="Ej: Compartimento lateral izquierdo"
                            />
                        </div>

                        {/* Observaciones */}
                        <div className="space-y-2">
                            <Label>Observaciones (opcional)</Label>
                            <Textarea
                                value={itemForm.observaciones}
                                onChange={(e) => setItemForm(prev => ({ ...prev, observaciones: e.target.value }))}
                                rows={2}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowEditItemDialog(false)}>Cancelar</Button>
                        <Button onClick={handleUpdateItem} disabled={saving}>
                            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            Guardar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
        </>
    );
}
